/**
 * ProfileSetupStep — step 2 of the onboarding wizard.
 * Collects display_name, role, company, industry, jurisdiction.
 * Submits via form ref — wizard footer button calls formRef.current.requestSubmit().
 */

import { useState } from 'react';
import type { ProfileFormData, ProfileFieldErrors } from './types';

export const INDUSTRIES = [
  'Accounting', 'Legal', 'Consulting', 'Healthcare',
  'Financial Services', 'Technology', 'Other',
] as const;

export const JURISDICTIONS = ['AU', 'NZ', 'UK', 'US', 'CA', 'SG', 'Other'] as const;

const ROLE_OPTIONS = [
  {
    value: 'firm_admin' as const,
    label: 'Firm Administrator',
    description: 'Manage users, settings, and billing',
  },
  {
    value: 'practitioner' as const,
    label: 'Practitioner',
    description: 'Day-to-day work and client management',
  },
  {
    value: 'it_admin' as const,
    label: 'IT Administrator',
    description: 'Technical setup and integrations',
  },
];

const EMPTY_FORM: ProfileFormData = {
  display_name: '',
  role: 'practitioner',
  company: '',
  industry: null,
  jurisdiction: null,
};

interface ProfileSetupStepProps {
  /** Ref passed to the inner <form> so the wizard can call requestSubmit(). */
  formRef: React.RefObject<HTMLFormElement>;
  initialData: ProfileFormData | null;
  isSubmitting: boolean;
  /** Server-side field errors returned from the API (keyed by field name). */
  fieldErrors: ProfileFieldErrors;
  /** Called with valid, client-validated data. API call is the wizard's responsibility. */
  onValidSubmit: (data: ProfileFormData) => void;
}

function validate(form: ProfileFormData): ProfileFieldErrors {
  const errs: ProfileFieldErrors = {};
  if (!form.display_name.trim()) errs.display_name = 'Display name is required';
  return errs;
}

export function ProfileSetupStep({
  formRef,
  initialData,
  isSubmitting,
  fieldErrors,
  onValidSubmit,
}: ProfileSetupStepProps) {
  const [form, setForm] = useState<ProfileFormData>(initialData ?? EMPTY_FORM);
  const [localErrors, setLocalErrors] = useState<ProfileFieldErrors>({});

  // Server errors override local errors for the same field
  const errors: ProfileFieldErrors = { ...localErrors, ...fieldErrors };

  function handleChange(field: keyof ProfileFormData, value: string | null) {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    setLocalErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setLocalErrors(errs);
      return;
    }
    onValidSubmit(form);
  }

  const inputClass = (hasError: boolean) =>
    [
      'mt-1 block w-full rounded-md border px-3 py-2 text-sm text-gray-900',
      'placeholder-gray-400 focus:outline-none focus:ring-1',
      hasError
        ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
        : 'border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-500',
      isSubmitting ? 'cursor-not-allowed bg-gray-50' : '',
    ].join(' ');

  return (
    <form
      ref={formRef}
      id="profile-form"
      onSubmit={handleSubmit}
      noValidate
      className="mx-auto max-w-lg space-y-5 px-4 py-6"
    >
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Set up your profile</h2>
        <p className="mt-1 text-sm text-gray-500">
          Tell us a bit about yourself so we can personalise Martha for you.
        </p>
      </div>

      {/* Display Name */}
      <div>
        <label htmlFor="display_name" className="block text-sm font-medium text-gray-700">
          Display Name <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="display_name"
          type="text"
          value={form.display_name}
          onChange={(e) => handleChange('display_name', e.target.value)}
          maxLength={100}
          disabled={isSubmitting}
          placeholder="Jane Smith"
          aria-required="true"
          aria-describedby={errors.display_name ? 'display_name-error' : undefined}
          aria-invalid={!!errors.display_name}
          className={inputClass(!!errors.display_name)}
        />
        {errors.display_name && (
          <p id="display_name-error" className="mt-1 text-xs text-red-600" role="alert">
            {errors.display_name}
          </p>
        )}
      </div>

      {/* Role */}
      <fieldset>
        <legend className="mb-2 block text-sm font-medium text-gray-700">
          Role <span className="text-red-500" aria-hidden="true">*</span>
        </legend>
        <div className="space-y-2">
          {ROLE_OPTIONS.map(({ value, label, description }) => (
            <label
              key={value}
              className={[
                'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                form.role === value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300',
                isSubmitting ? 'cursor-not-allowed opacity-60' : '',
              ].join(' ')}
            >
              <input
                type="radio"
                name="role"
                value={value}
                checked={form.role === value}
                onChange={() => handleChange('role', value)}
                disabled={isSubmitting}
                className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-800">{label}</span>
                <p className="text-xs text-gray-500">{description}</p>
              </div>
            </label>
          ))}
        </div>
        {errors.role && (
          <p className="mt-1 text-xs text-red-600" role="alert">{errors.role}</p>
        )}
      </fieldset>

      {/* Company */}
      <div>
        <label htmlFor="company" className="block text-sm font-medium text-gray-700">
          Company{' '}
          <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <input
          id="company"
          type="text"
          value={form.company}
          onChange={(e) => handleChange('company', e.target.value)}
          maxLength={200}
          disabled={isSubmitting}
          placeholder="ACME Corp"
          className={inputClass(false)}
        />
      </div>

      {/* Industry & Jurisdiction */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
            Industry
          </label>
          <select
            id="industry"
            value={form.industry ?? ''}
            onChange={(e) => handleChange('industry', e.target.value || null)}
            disabled={isSubmitting}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50"
          >
            <option value="">Select…</option>
            {INDUSTRIES.map((ind) => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="jurisdiction" className="block text-sm font-medium text-gray-700">
            Jurisdiction
          </label>
          <select
            id="jurisdiction"
            value={form.jurisdiction ?? ''}
            onChange={(e) => handleChange('jurisdiction', e.target.value || null)}
            disabled={isSubmitting}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50"
          >
            <option value="">Select…</option>
            {JURISDICTIONS.map((j) => (
              <option key={j} value={j}>{j}</option>
            ))}
          </select>
        </div>
      </div>
    </form>
  );
}
